import type { intelligence } from '@cwos/api-client';
import { countPhrase, formatPeriod, resourceWords } from '../evidenceView.js';

/**
 * results/evidence.ts — the typed result blocks' provenance, in product
 * language (F235).
 *
 * ───────────────────────────────────────────────────────────────────────────
 * WHAT THIS FILE REPLACED, AND WHY.
 *
 * The conversation surface stopped speaking the runtime's vocabulary in
 * `evidenceView.ts`. The RESULT BLOCKS did not, because they were a different
 * lane's subtree, and the sweep that guards the rest of the screen said so out
 * loud: it removed `results/**` from the DOM before asserting. So the KPI card
 * shipped, live, exactly the strings the makeover exists to delete:
 *
 *     Evidence — 3 classes
 *     OPERATIONAL   412 records — 412 time_session
 *     CATALOG       metric: hours_worked, computed by: dashboard analytics
 *                   engine (hours_worked)
 *     SUPPORTING    analytics.query_metric@1
 *
 * Every token there is a fact about the implementation. "Class" is how the
 * analytics envelope partitions its own evidence; `time_session` is a table;
 * `metric: hours_worked` is a catalog key with a colon in front of it; and
 * `analytics.query_metric@1` is a manifest reference — a correlation id in the
 * OLD derived notation, which since the evidence-issuance work does not even
 * address anything a person could look up.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * THE VOCABULARY IS IMPORTED, NOT FORKED.
 *
 * `countPhrase`, `resourceWords` and `formatPeriod` come from
 * `../evidenceView.js`, which is the one place a resource type becomes English
 * and the one place a date range becomes "Aug 17–23". A second table here would
 * drift from that one within a release, and the two firewalls it documents —
 * no resource type reaches a reader, no tool id reaches a reader — would then
 * hold on one half of the screen and not the other. This file adds only the
 * vocabulary that surface genuinely does not have: the evidence CLASS enum,
 * which exists on result blocks and nowhere else.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * WHAT IS RENDERED, AND WHAT IS DELIBERATELY NOT.
 *
 * Rendered: the counts, translated ("412 time records"); the metric, by its own
 * friendly label ("Hours worked"); the resolved window ("Aug 17–23").
 *
 * NOT rendered: `AnalyticsResultEvidence.supporting`. Those strings are
 * manifest references — the same correlation ids the answer cites, in whichever
 * notation the server issued them. They are verification material for an audit,
 * not information for a person, and this is the same call `evidenceView.ts`
 * makes about scope uuids and content hashes. Their PRESENCE still counts: a
 * figure backed only by supporting refs is not "no records were consulted", and
 * says so in words.
 */

// ---------------------------------------------------------------------------
// The key-shape guard for server-authored free text.
// ---------------------------------------------------------------------------

/**
 * True when a string that is supposed to be plain words is carrying an
 * identifier instead.
 *
 * `ResultEvidenceItem.label` is contractually "what was consulted, in plain
 * words", and mostly is. But it is free text composed on the server, and the
 * cheap way for it to go wrong is for a composer to interpolate the thing it
 * has to hand — a resource type, a metric key, a manifest ref. This catches the
 * SHAPES that are never English:
 *
 *   - snake_case (`time_session`, `total_hours_worked`)
 *   - a dotted lowercase identifier (`analytics.query_metric`)
 *   - a manifest ordinal (`@2`)
 *   - a hex run or a uuid (a content hash, an id)
 *   - a `domain:action` permission key
 *
 * A label that trips any of them is replaced by its class's neutral noun rather
 * than shown — the same trade `evidenceView.ts` makes for an unmapped resource
 * type, and for the same reason: a phrase that admits ignorance cannot be
 * wrong, and a key printed at somebody always is.
 */
export function looksLikeIdentifier(text: string): boolean {
  return (
    /[a-z0-9]_[a-z0-9]/i.test(text) ||
    /\b[a-z][a-z0-9]*\.[a-z][a-z0-9]{2,}\b/.test(text) ||
    /@\d/.test(text) ||
    /\b[0-9a-f]{8,}\b/i.test(text) ||
    /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/i.test(text) ||
    /\b[a-z]+:[a-z_-]+\b/.test(text)
  );
}

/**
 * `total_hours_worked` → "Total hours worked". Never the raw key.
 *
 * Dots are separators here as well as underscores, because a metric key is not
 * guaranteed to be a bare token — `analytics.hours_worked` humanized on
 * underscores alone would still ship a dotted identifier, which is the exact
 * shape a tool id has.
 */
export function humanizeMetricKey(key: string): string {
  const spaced = key
    .replace(/[._-]+/g, ' ')
    .trim()
    .toLowerCase();
  if (spaced === '') return 'Measure';
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * The metric's friendly label, never its catalog key.
 *
 * The server sends both (`AnalyticsKpi.metric` and `.label`) and the label is
 * normally real English. The guard is for the composer that fills `label` from
 * the key when the catalog has no display name — which is not hypothetical, it
 * is the natural one-line fallback — and would otherwise put `hours_worked` on
 * screen through a field named "label".
 */
export function metricLabel(kpi: intelligence.AnalyticsKpi): string {
  return looksLikeIdentifier(kpi.label) ? humanizeMetricKey(kpi.metric) : kpi.label;
}

/**
 * The resolved window in words: "Last week", else "Aug 17–23", else null.
 *
 * The server's own `periodLabel` wins when it sent one. Otherwise the
 * boundaries are formatted through `evidenceView.formatPeriod`, which is
 * deterministic and timezone-proof — the raw `2026-08-17 – 2026-08-23` this
 * replaces was not a leak of an identifier, but it was the wire's spelling of a
 * date on a line a person reads.
 */
export function periodPhrase(
  period: { from: string; to: string },
  periodLabel: string | undefined,
): string | null {
  if (periodLabel !== undefined && periodLabel.trim() !== '') return periodLabel;
  return formatPeriod(period.from, period.to);
}

// ---------------------------------------------------------------------------
// The evidence classes result blocks carry.
// ---------------------------------------------------------------------------

/**
 * `ResultEvidenceClass` → the eyebrow a person reads.
 *
 * Four values, closed union, exhaustive by type — so unlike a resource type
 * there is no "unmapped" case the server can invent under us. The neutral
 * fallback below is for a payload that got past the schema anyway, not for a
 * gap in this table.
 *
 * The words are nouns for the SOURCE, not names for the partition: a reader
 * needs to know a line came from their own records rather than from a policy
 * document, and does not need to know the envelope calls that "class".
 */
const CLASS_WORDS: Readonly<Record<intelligence.ResultEvidenceClass, string>> = {
  operational: 'Records',
  policy: 'Policies',
  uploaded: 'Files you provided',
  catalog: 'Metric definitions',
};

/** The honest degradation for a class this build has no word for. */
const NEUTRAL_CLASS = 'Records';

/** The eyebrow for one evidence class. Never the enum value. */
export function classWords(evidenceClass: string): string {
  return Object.prototype.hasOwnProperty.call(CLASS_WORDS, evidenceClass)
    ? CLASS_WORDS[evidenceClass as intelligence.ResultEvidenceClass]
    : NEUTRAL_CLASS;
}

/**
 * The resource type whose noun stands in for a class when its label cannot be
 * shown — routed through `evidenceView`'s table so the fallback noun is the
 * same word the conversation surface would have used.
 */
const CLASS_RESOURCE: Readonly<Record<intelligence.ResultEvidenceClass, string>> = {
  operational: 'rows',
  policy: 'knowledge_article',
  uploaded: 'rows',
  catalog: 'metric_catalog',
};

function classResource(evidenceClass: string): string {
  return Object.prototype.hasOwnProperty.call(CLASS_RESOURCE, evidenceClass)
    ? CLASS_RESOURCE[evidenceClass as intelligence.ResultEvidenceClass]
    : 'rows';
}

// ---------------------------------------------------------------------------
// The lines themselves.
// ---------------------------------------------------------------------------

/** One provenance line on a result block, already in product language. */
export interface ResultEvidenceLine {
  /** Stable render key. Positional — the payload's order is the server's. */
  readonly key: string;
  /** The source, as an eyebrow noun. Never an enum value. */
  readonly eyebrow: string;
  /** The words. Never a key, an ordinal, a hash or a raw resource type. */
  readonly text: string;
}

/**
 * A KPI's evidence as lines a person can read.
 *
 *   Metric definitions · Hours worked
 *   Records           · 412 time records
 *
 * The catalog line names the METRIC, by its label, because that is what those
 * catalog entries actually identify — `metric: hours_worked` and `computed by:
 * dashboard analytics engine (hours_worked)` are two spellings of "this figure
 * came from the hours-worked definition", and the label says it in one.
 *
 * Operational entries are counted and translated one line each; an entry with
 * no rows is dropped rather than shown as "0 records", which reads as a
 * measurement rather than as an empty retrieval.
 *
 * Supporting refs are not rendered — see the module docstring — but a figure
 * standing only on them still gets a line, because "computed, detail not
 * retained" is a real state and is not the same answer as "nothing was
 * consulted".
 *
 * Returns [] only when the payload carries no evidence of any class at all,
 * which the card renders as its own sentence.
 */
export function kpiEvidenceLines(kpi: intelligence.AnalyticsKpi): ResultEvidenceLine[] {
  const lines: ResultEvidenceLine[] = [];

  if (kpi.evidence.catalog.length > 0) {
    lines.push({ key: 'catalog', eyebrow: classWords('catalog'), text: metricLabel(kpi) });
  }

  for (const [index, entry] of kpi.evidence.operational.entries()) {
    if (entry.count <= 0) continue;
    lines.push({
      key: `operational:${index}`,
      eyebrow: classWords('operational'),
      text: countPhrase(entry.resourceType, entry.count),
    });
  }

  const hasCounts = lines.some((line) => line.key.startsWith('operational:'));
  if (!hasCounts && kpi.evidence.supporting.length > 0) {
    lines.push({
      key: 'supporting',
      eyebrow: classWords('operational'),
      text: 'Worked out from your organization’s records',
    });
  }

  const period = periodPhrase(kpi.period, kpi.periodLabel);
  if (lines.length > 0 && period !== null) {
    lines.push({ key: 'period', eyebrow: 'Period', text: period });
  }

  return lines;
}

/**
 * Classed evidence on a table, in product language.
 *
 * The class becomes its eyebrow noun. The label is shown when it is plain
 * words and replaced by the class's own noun when it is an identifier wearing a
 * label's name — see {@link looksLikeIdentifier}. A count, when the server sent
 * one, is spelled with the noun for its class ("41 records", "3 policies")
 * rather than left as a bare parenthesised number.
 */
export function itemEvidenceLines(
  items: readonly intelligence.ResultEvidenceItem[],
): ResultEvidenceLine[] {
  return items.map((item, index) => {
    const [, plural] = resourceWords(classResource(item.class));
    const text = looksLikeIdentifier(item.label) ? plural : item.label;
    const counted =
      item.count !== undefined
        ? `${text} · ${countPhrase(classResource(item.class), item.count)}`
        : text;
    return { key: `${index}`, eyebrow: classWords(item.class), text: counted };
  });
}
